import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field } from '@/components/ui/form-field';
import { Checkbox, Input, Select, Textarea } from '@/components/ui/primitives';
import { api, ApiError, errorMessage } from '@/lib/api';
import type { ContactItem, CustomerDetail } from '@/lib/types';

type CustomerForm = {
  type: 'PRIVATE' | 'BUSINESS';
  salutation: '' | 'MR' | 'MRS' | 'FAMILY';
  companyName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  mobile: string;
  whatsappOptIn: boolean;
  street: string;
  postalCode: string;
  city: string;
  country: string;
  vatId: string;
  notes: string;
};

const empty: CustomerForm = {
  type: 'BUSINESS',
  salutation: '',
  companyName: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  mobile: '',
  whatsappOptIn: false,
  street: '',
  postalCode: '',
  city: '',
  country: 'DE',
  vatId: '',
  notes: '',
};

function fieldErrors(e: unknown) {
  const out: Record<string, string> = {};
  if (e instanceof ApiError) for (const d of e.details ?? []) out[d.path] = d.message;
  return out;
}

export function CustomerDialog({
  customer,
  open,
  onOpenChange,
  onSaved,
}: {
  customer: CustomerDetail | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: (c: CustomerDetail) => void;
}) {
  const [f, setF] = useState<CustomerForm>(empty);
  useEffect(() => {
    if (!open) return;
    setF(
      customer
        ? Object.fromEntries(Object.entries(empty).map(([k, v]) => [k, (customer as unknown as Record<string, unknown>)[k] ?? v])) as CustomerForm
        : empty,
    );
  }, [open, customer]);
  const set = (k: keyof CustomerForm) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: () =>
      customer
        ? api<CustomerDetail>(`/customers/${customer.id}`, { method: 'PUT', body: { ...f, salutation: f.type === 'PRIVATE' && f.salutation ? f.salutation : null } })
        : api<CustomerDetail>('/customers', { method: 'POST', body: { ...f, salutation: f.type === 'PRIVATE' && f.salutation ? f.salutation : null } }),
    onSuccess: (c) => {
      toast.success(customer ? 'Kunde gespeichert' : `Kunde ${c.number} angelegt`);
      onSaved(c);
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const err = fieldErrors(save.error);
  const business = f.type === 'BUSINESS';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent wide>
        <form
          className="flex min-h-0 flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>{customer ? `Kunde ${customer.number} bearbeiten` : 'Neuer Kunde'}</DialogTitle>
            {!customer && <DialogDescription>Die Kundennummer wird automatisch vergeben.</DialogDescription>}
          </DialogHeader>
          <DialogBody className="grid gap-4 sm:grid-cols-2">
            <Field label="Kundenart" htmlFor="c-type">
              <Select id="c-type" value={f.type} onChange={set('type')}>
                <option value="BUSINESS">Geschäftskunde</option>
                <option value="PRIVATE">Privatkunde</option>
              </Select>
            </Field>
            {business ? (
              <Field label="Firma" htmlFor="c-company" error={err.companyName}>
                <Input id="c-company" value={f.companyName} onChange={set('companyName')} autoFocus />
              </Field>
            ) : (
              <Field label="Anrede" htmlFor="c-salutation">
                <Select id="c-salutation" value={f.salutation} onChange={set('salutation')}>
                  <option value="">– keine –</option>
                  <option value="MRS">Frau</option>
                  <option value="MR">Herr</option>
                  <option value="FAMILY">Familie</option>
                </Select>
              </Field>
            )}
            <Field label="Vorname" htmlFor="c-first">
              <Input id="c-first" value={f.firstName} onChange={set('firstName')} />
            </Field>
            <Field label="Nachname" htmlFor="c-last" error={err.lastName}>
              <Input id="c-last" value={f.lastName} onChange={set('lastName')} />
            </Field>
            <Field label="E-Mail" htmlFor="c-email" error={err.email}>
              <Input id="c-email" type="email" value={f.email} onChange={set('email')} />
            </Field>
            <Field label="Telefon" htmlFor="c-phone">
              <Input id="c-phone" value={f.phone} onChange={set('phone')} />
            </Field>
            <Field label="Mobil" htmlFor="c-mobile">
              <Input id="c-mobile" value={f.mobile} onChange={set('mobile')} />
            </Field>
            <label className="flex items-start gap-2 text-sm sm:col-span-2">
              <Checkbox className="mt-0.5" checked={f.whatsappOptIn} onCheckedChange={(c) => setF((x) => ({ ...x, whatsappOptIn: c === true }))} />
              <span>
                WhatsApp-Benachrichtigungen an die Mobilnummer (Einwilligung liegt vor)
                <span className="block text-xs text-muted-foreground">z. B. Ticket-/Reparaturnummer bei Eröffnung und „abholbereit“. Nur mit dokumentierter Zustimmung des Kunden.</span>
              </span>
            </label>
            {business ? (
              <Field label="USt-IdNr." htmlFor="c-vat">
                <Input id="c-vat" value={f.vatId} onChange={set('vatId')} />
              </Field>
            ) : (
              <div />
            )}
            <Field label="Straße und Hausnummer" htmlFor="c-street" className="sm:col-span-2">
              <Input id="c-street" value={f.street} onChange={set('street')} />
            </Field>
            <div className="grid grid-cols-[6rem_1fr_4rem] gap-3 sm:col-span-2">
              <Field label="PLZ" htmlFor="c-zip">
                <Input id="c-zip" value={f.postalCode} onChange={set('postalCode')} />
              </Field>
              <Field label="Ort" htmlFor="c-city">
                <Input id="c-city" value={f.city} onChange={set('city')} />
              </Field>
              <Field label="Land" htmlFor="c-country">
                <Input id="c-country" value={f.country} maxLength={2} onChange={set('country')} />
              </Field>
            </div>
            <Field label="Notizen (intern)" htmlFor="c-notes" className="sm:col-span-2">
              <Textarea id="c-notes" rows={3} value={f.notes} onChange={set('notes')} />
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" loading={save.isPending}>
              Speichern
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

type ContactForm = { firstName: string; lastName: string; email: string; phone: string; mobile: string; whatsappOptIn: boolean; position: string; isPrimary: boolean; notes: string };

export function ContactDialog({
  customerId,
  contact,
  open,
  onOpenChange,
  onSaved,
}: {
  customerId: string;
  contact: ContactItem | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: (c: CustomerDetail) => void;
}) {
  const [f, setF] = useState<ContactForm>({ firstName: '', lastName: '', email: '', phone: '', mobile: '', whatsappOptIn: false, position: '', isPrimary: false, notes: '' });
  useEffect(() => {
    if (!open) return;
    setF({
      firstName: contact?.firstName ?? '',
      lastName: contact?.lastName ?? '',
      email: contact?.email ?? '',
      phone: contact?.phone ?? '',
      mobile: contact?.mobile ?? '',
      whatsappOptIn: contact?.whatsappOptIn ?? false,
      position: contact?.position ?? '',
      isPrimary: contact?.isPrimary ?? false,
      notes: contact?.notes ?? '',
    });
  }, [open, contact]);
  const set = (k: keyof ContactForm) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = useMutation({
    mutationFn: () =>
      contact
        ? api<CustomerDetail>(`/customers/${customerId}/contacts/${contact.id}`, { method: 'PUT', body: f })
        : api<CustomerDetail>(`/customers/${customerId}/contacts`, { method: 'POST', body: f }),
    onSuccess: (c) => {
      onSaved(c);
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          className="flex min-h-0 flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>{contact ? 'Ansprechpartner bearbeiten' : 'Neuer Ansprechpartner'}</DialogTitle>
          </DialogHeader>
          <DialogBody className="grid gap-4 sm:grid-cols-2">
            <Field label="Vorname" htmlFor="p-first">
              <Input id="p-first" value={f.firstName} onChange={set('firstName')} autoFocus />
            </Field>
            <Field label="Nachname" htmlFor="p-last">
              <Input id="p-last" value={f.lastName} onChange={set('lastName')} />
            </Field>
            <Field label="E-Mail" htmlFor="p-email">
              <Input id="p-email" type="email" value={f.email} onChange={set('email')} />
            </Field>
            <Field label="Position" htmlFor="p-pos">
              <Input id="p-pos" value={f.position} onChange={set('position')} />
            </Field>
            <Field label="Telefon" htmlFor="p-phone">
              <Input id="p-phone" value={f.phone} onChange={set('phone')} />
            </Field>
            <Field label="Mobil" htmlFor="p-mobile">
              <Input id="p-mobile" value={f.mobile} onChange={set('mobile')} />
            </Field>
            <label className="flex items-start gap-2 text-sm sm:col-span-2">
              <Checkbox className="mt-0.5" checked={f.whatsappOptIn} onCheckedChange={(c) => setF((x) => ({ ...x, whatsappOptIn: c === true }))} />
              <span>
                WhatsApp-Benachrichtigungen an die Mobilnummer (Einwilligung liegt vor)
                <span className="block text-xs text-muted-foreground">z. B. Ticket-/Reparaturnummer bei Eröffnung und „abholbereit“. Nur mit dokumentierter Zustimmung des Kunden.</span>
              </span>
            </label>
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <Checkbox checked={f.isPrimary} onCheckedChange={(c) => setF((x) => ({ ...x, isPrimary: c === true }))} />
              Hauptansprechpartner
            </label>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" loading={save.isPending} disabled={!f.firstName.trim() || !f.lastName.trim()}>
              Speichern
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
